<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :style="{ width: '450px' }"
    :header="t('campaignsAutomationList.actions.deactivate')"
  >
    <div class="p-fluid">
      <p>{{ t('campaignsAutomationList.actions.deactivateConfirm') }}</p>
      <p
        v-if="currentCampaign"
        class="font-semibold mt-2"
      >
        {{ currentCampaign.name }}
      </p>
    </div>
    <template #footer>
      <SpmButton
        :label="t('no')"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('yes')"
        icon="pi pi-check"
        class-style="p-button p-button-danger"
        :disabled="isLoading"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { UserState } from '@/composables/User';
import { CampaignAutomationStatusEnum } from '@/types';
import {
  updateCampaignAutomationListColumn,
  setRefreshScenariosList,
  toggleWebhooksForAutomationCampaign,
  togglePeriodicTriggersForAutomationCampaign,
} from '@/composables/AutomatedScenarios';
import type { CampaignsAutomationUpdateInputItem } from '@/types/generated-types/graphql';
import type { AutomationCampaignData, CampaignActionPayload, DeactivateCampaignResult } from './types';

export default defineComponent({
  name: 'DeactivateCampaignAction',

  components: {
    Dialog,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const isLoading = ref(false);
    const currentCampaign = ref<AutomationCampaignData | null>(null);
    const idShop = ref<number>(UserState.activeShop?.id ?? 0);

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: CampaignActionPayload) => {
      currentCampaign.value = payload.campaign;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentCampaign.value = null;
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = async () => {
      if (!currentCampaign.value) return;

      isLoading.value = true;

      try {
        const input: CampaignsAutomationUpdateInputItem = {
          id_campaign_automation: currentCampaign.value.id,
          id_shop: idShop.value,
          status: CampaignAutomationStatusEnum.DISABLED,
          date_modification: moment().format('YYYY-MM-DD HH:mm:ss'),
        };

        await updateCampaignAutomationListColumn([input]);
        // Activation/deactivation of workflow's webhooks
        toggleWebhooksForAutomationCampaign(currentCampaign.value.id, false);
        // Activation/deactivation of workflow's periodic triggers
        togglePeriodicTriggersForAutomationCampaign(currentCampaign.value.id, false);
        showToastSuccess(t('campaignsAutomationList.actions.deactivateSuccess'));
        setRefreshScenariosList(true);

        const result: DeactivateCampaignResult = {
          campaign: currentCampaign.value,
          success: true,
        };

        emit('confirm', result);
        close();
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : t('errorMessages.GENERIC_ERROR');
        showToastError(errorMessage);
      } finally {
        isLoading.value = false;
      }
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      isLoading,
      currentCampaign,
      close,
      confirm,
    };
  },
});
</script>
